import {
  DataGridPro,
  DataGridProProps,
  GridColDef,
  GridPaginationModel,
  GridValidRowModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import {
  notification_colorAlert,
  tag_colorTagHighlightText,
  warning_border,
} from 'ext-ui/style/colors'
import moment from 'moment'
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { chevronColumnDef } from 'src/components/DataGrid/cells/ChevronCell'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import { renderHeader } from 'src/components/DataGrid/cells/DataGridHeaderCell/DataGridHeaderCell'
import IconsCell from 'src/components/DataGrid/cells/IconsCell'
import PillCell from 'src/components/DataGrid/cells/PillCell/PillCell'
import { DataGridRowHeight } from 'src/components/DataGrid/DataGrid'
import {
  ExpenseReportsFiltersBody,
  ExpenseReportsListCheckboxProps,
} from 'src/components/ExpenseReportsBox/ExpenseReportsBox'
import SvgAlertCircled from 'src/components/Svgs/SvgAlertCircled'
import SvgClock from 'src/components/Svgs/SvgClock'
import SvgFlag from 'src/components/Svgs/SvgFlag'
import SvgNewCheck from 'src/components/Svgs/SvgNewCheck'
import { ExtendCurrency } from 'src/enums'
import Crncy from 'src/localization/Crncy'
import { translateSingleString } from 'src/localization/Trns'
import {
  ExpenseReportManagerReviewStatus,
  ExpenseReportModel,
  ExpenseReportStatus,
} from 'src/models/expenseReport.model'
import { connectedPush } from 'src/navigation/Lnk'
import {
  expenseReportUrl,
  managerReviewReimbursementURL,
  myWalletReimbursementURL,
} from 'src/navigation/urls'
import { assertNever } from 'src/queries/agent/network/agent.network'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { InfiniteScrollProps } from 'src/types'
import { toDollars } from 'src/utils/currency.utils'
import { css } from 'styled-components'

import TableEmptyState, {
  TableEmptyStateDesigns,
} from '../TableEmptyState/TableEmptyState'

type OOPEExpensesDataGridProps = {
  expenseReports: ExpenseReportModel[]
  infiniteScrollProps: InfiniteScrollProps<ExpenseReportsFiltersBody>
  filters: ExpenseReportsFiltersBody
  checkboxProps?: ExpenseReportsListCheckboxProps
  context: 'submitter' | 'approver' | 'manager'
}

interface OOPEReportsDataGridRowModel extends GridValidRowModel {
  expenseReport: ExpenseReportModel
  index: number
}

const SubmitExpensesDataGrid: React.FC<OOPEExpensesDataGridProps> = (
  props,
): React.ReactElement => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const dispatch = useDispatch()

  const NoResultsMessage = (): ReactElement => (
    <div
      css={css`
        padding-top: 40px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
      `}
    >
      {translateSingleString(
        locale,
        'No results found. Please check your filters and try again.',
      )}
    </div>
  )

  const reportUrl = useCallback(
    (reportId: string) => {
      let url
      switch (props.context) {
        case 'submitter':
          url = myWalletReimbursementURL(reportId)
          break
        case 'manager':
          url = managerReviewReimbursementURL(reportId)
          break
        case 'approver':
          url = expenseReportUrl(reportId)
          break
      }
      return url
    },
    [props.context],
  )

  const rows: OOPEReportsDataGridRowModel[] = props.expenseReports.map(
    (expenseReport, i) => {
      return {
        id: expenseReport.id,
        expenseReport: expenseReport,
        index: i,
      }
    },
  )

  const columns: Array<GridColDef<OOPEReportsDataGridRowModel> | undefined> = [
    {
      field: 'date',
      headerName: translateSingleString(locale, 'Date'),
      groupable: false,
      minWidth: 150,
      hideable: false,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: renderHeader,
      renderCell: (params): React.ReactElement => {
        const expenseReport = params.row.expenseReport
        return (
          <DataGridCell
            bold
            text={
              expenseReport.submittedAtDate
                ? moment(expenseReport.submittedAtDate).format('l')
                : '--'
            }
            params={params}
          />
        )
      },
    },
    {
      field: 'report_name',
      headerName: translateSingleString(locale, 'Report Name'),
      flex: 1,
      minWidth: 150,
      hideable: false,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: renderHeader,
      renderCell: (params): React.ReactElement => {
        return (
          <DataGridCell
            text={params.row.expenseReport.reportName || '--'}
            params={params}
          />
        )
      },
    },
    props.context !== 'submitter'
      ? {
          field: 'employee',
          headerName: translateSingleString(locale, 'Employee'),
          flex: 1,
          minWidth: 150,
          hideable: false,
          sortable: false,
          disableColumnMenu: true,
          renderHeader: renderHeader,
          renderCell: (params): React.ReactElement => {
            return (
              <DataGridCell
                text={params.row.expenseReport.employee || '--'}
                params={params}
              />
            )
          },
        }
      : undefined,
    {
      field: 'amount_submitted',
      headerName: translateSingleString(locale, 'Amount Submitted'),
      flex: 1,
      minWidth: 150,
      hideable: false,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: renderHeader,
      renderCell: (params): React.ReactElement => {
        const expenseReport = params.row.expenseReport
        const amount =
          expenseReport.totalAmountSubmittedCents === 0 &&
          expenseReport.status !== ExpenseReportStatus.Draft ? (
            <Crncy currency={ExtendCurrency.Usd} amount={0} />
          ) : expenseReport.totalAmountSubmittedCents &&
            expenseReport.totalAmountSubmittedCurrency &&
            expenseReport.status !== ExpenseReportStatus.Draft ? (
            <Crncy
              currency={expenseReport.totalAmountSubmittedCurrency}
              amount={toDollars(expenseReport.totalAmountSubmittedCents)}
            />
          ) : (
            '--'
          )
        return <DataGridCell text={amount} params={params} />
      },
    },
    props.context !== 'manager'
      ? {
          field: 'amount_approved',
          headerName: translateSingleString(locale, 'Amount Approved'),
          flex: 1,
          minWidth: 150,
          hideable: false,
          sortable: false,
          disableColumnMenu: true,
          renderHeader: renderHeader,
          renderCell: (params): React.ReactElement => {
            const expenseReport = params.row.expenseReport
            const amount =
              expenseReport.status === ExpenseReportStatus.Rejected ||
              ((expenseReport.status === ExpenseReportStatus.Reviewed ||
                expenseReport.status === ExpenseReportStatus.Paid) &&
                expenseReport.totalAmountSubmittedCents === 0) ? (
                <Crncy currency={ExtendCurrency.Usd} amount={0} />
              ) : expenseReport.approvedAmountCents &&
                expenseReport.approvedAmountCurrency ? (
                <Crncy
                  currency={expenseReport.approvedAmountCurrency}
                  amount={toDollars(expenseReport.approvedAmountCents)}
                />
              ) : (
                '--'
              )
            return <DataGridCell text={amount} params={params} />
          },
        }
      : undefined,
    props.context === 'manager'
      ? {
          field: 'approval_status',
          headerName: translateSingleString(locale, 'Review Status'),
          minWidth: 150,
          sortable: false,
          hideable: false,
          disableColumnMenu: true,
          renderHeader: renderHeader,
          renderCell: (params): React.ReactElement => {
            return (
              <PillCell color={'pending'} params={params}>
                {translateSingleString(locale, 'Pending Review')}
              </PillCell>
            )
          },
        }
      : undefined,
    props.context === 'approver'
      ? {
          field: 'manager_approval_status',
          headerName: translateSingleString(locale, 'Manager Review'),
          minWidth: 150,
          sortable: false,
          hideable: false,
          disableColumnMenu: true,
          renderHeader: renderHeader,
          renderCell: (params): React.ReactElement => {
            const report = params.row.expenseReport
            let icon
            switch (report.managerReviewStatus) {
              case ExpenseReportManagerReviewStatus.Reviewed:
                icon = (
                  <SvgNewCheck
                    stroke={tag_colorTagHighlightText}
                    data-testid={'reviewed'}
                    css={css`
                      scale: calc(24 / 16);
                    `}
                  />
                )
                break
              case ExpenseReportManagerReviewStatus.Rejected:
                icon = (
                  <SvgFlag
                    stroke={notification_colorAlert}
                    data-testid={'flagged'}
                    css={css`
                      scale: calc(22 / 16);
                    `}
                  />
                )
                break
              case ExpenseReportManagerReviewStatus.Mixed:
                icon = (
                  <SvgAlertCircled
                    fill={warning_border}
                    data-testid={'mixed'}
                  />
                )
                break
              default:
                icon = (
                  <SvgClock
                    data-testid={'pendingReviewIcon'}
                    css={css`
                      scale: calc(22 / 20);
                    `}
                  />
                )
                break
            }
            return <IconsCell icon={icon} params={params} />
          },
        }
      : undefined,
    props.context !== 'manager'
      ? {
          field: 'status',
          headerName: translateSingleString(
            locale,
            props.context === 'approver' ? 'Final Approval' : 'Status',
          ),
          flex: 1,
          minWidth: 150,
          hideable: false,
          sortable: false,
          disableColumnMenu: true,
          renderHeader: renderHeader,
          renderCell: (params): React.ReactElement => {
            const report = params.row.expenseReport
            let pillColor: 'pending' | 'highlight' | 'severe' | undefined
            let pillTextSrc: string

            switch (report.status) {
              case ExpenseReportStatus.Draft:
                pillTextSrc = 'Not Submitted'
                break
              case ExpenseReportStatus.Pending:
                pillColor = 'pending'
                pillTextSrc =
                  props.context !== 'submitter'
                    ? 'Pending Approval'
                    : 'Submitted'
                break
              case ExpenseReportStatus.Reviewed:
                pillColor = 'pending'
                pillTextSrc = 'Approved'
                break
              case ExpenseReportStatus.Rejected:
                pillColor = 'severe'
                pillTextSrc = 'Rejected'
                break
              case ExpenseReportStatus.Paid:
                pillColor = 'highlight'
                pillTextSrc = 'Paid'
                break
              default:
                assertNever(report.status)
            }
            return (
              <PillCell color={pillColor} params={params}>
                {translateSingleString(locale, pillTextSrc)}
              </PillCell>
            )
          },
        }
      : undefined,
    chevronColumnDef,
  ]

  const columnsSafe = useMemo(
    () =>
      columns.filter((column) => column !== undefined) as Array<
        GridColDef<OOPEReportsDataGridRowModel>
      >,
    [columns],
  )

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  })

  // custom event implementations from https://mui.com/x/react-data-grid/events/#catalog-of-events
  const apiRef = useGridApiRef()
  useEffect(() => {
    const unsubscribe =
      props.checkboxProps?.handleHeaderCheckboxClick &&
      apiRef.current?.subscribeEvent(
        'headerSelectionCheckboxChange',
        props.checkboxProps?.handleHeaderCheckboxClick,
      )

    return () => {
      unsubscribe?.()
    }
  }, [apiRef.current, props.checkboxProps?.handleHeaderCheckboxClick])

  // calculate window height to properly set the table height
  // table height must be set for the infinite scroll to work properly
  const [clientHeight, setClientHeight] = useState<number | null>(null)
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)
  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })

  useEffect(() => {
    setClientHeight(viewportHeight.height - 122)
  }, [ref?.current])

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    setClientHeight(viewportHeight.height - 122)
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight, ref?.current])

  // handle infinite scroll
  const handleOnRowsScrollEnd = useCallback<
    NonNullable<DataGridProProps['onRowsScrollEnd']>
  >((): void => {
    if (
      !props?.infiniteScrollProps?.loading &&
      !props?.infiniteScrollProps?.complete
    ) {
      const nextPage =
        props?.infiniteScrollProps?.page !== undefined
          ? props?.infiniteScrollProps?.page + 1
          : undefined
      props?.infiniteScrollProps?.fetch(props.filters, nextPage)
    }
  }, [
    props.filters,
    props?.infiniteScrollProps?.fetch,
    props?.infiniteScrollProps?.loading,
    props?.infiniteScrollProps?.complete,
    props?.infiniteScrollProps?.page,
    paginationModel.pageSize,
  ])

  return (
    <div
      ref={ref}
      css={css`
        width: 100%;
        height: ${(clientHeight || 0) - 60}px;
      `}
    >
      {/* TODO: this isn't using our pre-styled datagrid */}
      {/* when moved over, remove sx, clean up pinned columns, remove `renderHeader` from colDef */}
      <DataGridPro
        apiRef={apiRef}
        initialState={{
          pinnedColumns: {
            right: ['chevron'],
            left: ['__check__', 'date', 'report_name'],
          },
        }}
        rows={rows}
        columns={columnsSafe}
        loading={props.infiniteScrollProps.loading}
        sx={{
          // sticky column styling
          '.MuiDataGrid-cell': {
            backgroundColor: '#ffffff',
            padding: 0,
          },
          '.MuiDataGrid-filler': {
            backgroundColor: '#ffffff',
          },
          // hide border on pinned right column
          // hide padding on pinned right column (chevron cell sets its own border)
          '.MuiDataGrid-cell--pinnedRight, .MuiDataGrid-filler--pinnedRight': {
            paddingLeft: '0px',
            borderLeft: 'unset',
          },
          // hide border on pinned left column
          '.MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-filler--pinnedLeft': {
            borderRight: 'unset',
          },
          // row hover color
          '.MuiDataGrid-row:hover': {
            '.MuiDataGrid-cell, .MuiDataGrid-cell--pinnedRight, .MuiDataGrid-cell--pinnedLeft':
              {
                backgroundColor: '#f5f5f5',
              },
          },
          '--DataGrid-overlayHeight': '0px',
          // Lower the scrollbar so it is below modals
          '.MuiDataGrid-scrollbar': {
            zIndex: 1,
          },
        }}
        rowHeight={DataGridRowHeight.Comfy}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[]} // need to pass an empty array to disable pagination options
        onRowsScrollEnd={handleOnRowsScrollEnd}
        slots={{
          loadingOverlay: () => <></>,
          noResultsOverlay: () => <NoResultsMessage />,
          noRowsOverlay: () =>
            props.context !== 'submitter' ? (
              <TableEmptyState
                design={TableEmptyStateDesigns.ExpensesNeedsApproval}
              />
            ) : (
              <NoResultsMessage />
            ),
        }}
        // row selection logic
        checkboxSelection={props.checkboxProps !== undefined}
        isRowSelectable={(params): boolean => {
          const expenseReport = params.row.expenseReport
          return !!props.checkboxProps?.canSelectExpenseReport(expenseReport)
        }}
        onRowSelectionModelChange={props.checkboxProps?.handleCheckboxClick}
        onRowClick={(row): void => {
          const expenseReport = row.row.expenseReport
          dispatch(
            connectedPush({
              pathname: reportUrl(expenseReport.id),
              search: '',
              hash: '',
              state: {},
            }),
          )
        }}
      />
    </div>
  )
}

export default SubmitExpensesDataGrid
